import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { employeesAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import ExportButton from '../components/ExportButton';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { 
  Search, 
  Plus, 
  Filter,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Loader2,
  KeyRound,
  UserPlus,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const Employees = () => {
  const { user } = useAuth();
  const canManage = ['super_admin', 'hr_admin'].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [accountStatus, setAccountStatus] = useState({}); // { emp_id: bool }
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Bulk account creation state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    employee_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_id: '',
    department: '',
    designation: '',
    join_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchData();
  }, [filterCompany, filterStatus]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterCompany !== 'all') params.company_id = filterCompany;
      if (filterStatus !== 'all') params.status = filterStatus;
      
      const [employeesRes, companiesRes] = await Promise.all([
        employeesAPI.getAll(params),
        companiesAPI.getAll()
      ]);
      setEmployees(employeesRes.data);
      setCompanies(companiesRes.data);
      if (canManage) {
        try {
          const st = await employeesAPI.userAccountStatusMap();
          setAccountStatus(st.data?.status || {});
        } catch (e) { /* non-blocking */ }
      }
    } catch (error) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (empId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) next.delete(empId); else next.add(empId);
      return next;
    });
  };

  const enterSelection = () => { setSelectionMode(true); setSelectedIds(new Set()); };
  const exitSelection = () => { setSelectionMode(false); setSelectedIds(new Set()); };

  const eligibleForAccountCreation = (emp) => !accountStatus[emp.id] && !!emp.email;

  const selectAllEligible = () => {
    const ids = new Set(filteredEmployees.filter(eligibleForAccountCreation).map((e) => e.id));
    setSelectedIds(ids);
  };

  const selectedEmployees = employees.filter((e) => selectedIds.has(e.id));
  const previewCreatable = selectedEmployees.filter(eligibleForAccountCreation);
  const previewSkippable = selectedEmployees.filter((e) => accountStatus[e.id]);
  const previewInvalid = selectedEmployees.filter((e) => !e.email);

  const openBulkPreview = () => {
    if (selectedIds.size === 0) { toast.error('Select at least one employee'); return; }
    setBulkResult(null);
    setBulkPreviewOpen(true);
  };

  const runBulkCreate = async () => {
    const ids = previewCreatable.map((e) => e.id);
    if (ids.length === 0) { toast.error('No eligible employees selected'); return; }
    setBulkBusy(true);
    try {
      const r = await employeesAPI.bulkCreateUserAccounts({ employee_ids: ids, role: 'employee', force_password_change: true, send_email: true });
      setBulkResult(r.data);
      toast.success(`${r.data.created} account(s) created, ${r.data.skipped} skipped, ${r.data.failed} failed`);
      // Refresh account status map
      try {
        const st = await employeesAPI.userAccountStatusMap();
        setAccountStatus(st.data?.status || {});
      } catch (e) { /* ignore */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk creation failed');
    } finally { setBulkBusy(false); }
  };

  const closeBulkAll = () => {
    setBulkPreviewOpen(false);
    setBulkResult(null);
    exitSelection();
  };

  const handleCreateEmployee = async () => {
    if (!newEmployee.company_id) {
      toast.error('Please select a company');
      return;
    }
    
    setSubmitting(true);
    try {
      await employeesAPI.create({
        ...newEmployee,
        status: 'active'
      });
      toast.success('Employee created successfully');
      setDialogOpen(false);
      setNewEmployee({
        employee_code: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_id: '',
        department: '',
        designation: '',
        join_date: new Date().toISOString().split('T')[0],
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchQuery.toLowerCase();
    return (
      emp.first_name.toLowerCase().includes(searchLower) ||
      emp.last_name.toLowerCase().includes(searchLower) ||
      emp.email.toLowerCase().includes(searchLower) ||
      emp.employee_code.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'on_leave': return 'warning';
      case 'terminated': return 'destructive';
      default: return 'secondary';
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Employees</h1>
          <p className="text-muted-foreground">Manage your workforce</p>
        </div>
        <div className="flex gap-2 flex-wrap">
        <ExportButton endpoint="/reports/employees/export" filename="employees" testId="export-employees" />
        {canManage && !selectionMode && (
          <Button variant="outline" onClick={enterSelection} data-testid="bulk-select-btn">
            <KeyRound size={16} className="mr-2" />
            Bulk Create Accounts
          </Button>
        )}
        {canManage && selectionMode && (
          <>
            <Button variant="outline" onClick={selectAllEligible} data-testid="select-all-eligible-btn">
              Select all eligible
            </Button>
            <Button variant="ghost" onClick={exitSelection} data-testid="cancel-selection-btn">
              Cancel
            </Button>
            <Button onClick={openBulkPreview} disabled={selectedIds.size === 0} data-testid="review-bulk-btn">
              <UserPlus size={16} className="mr-2" />
              Review ({selectedIds.size})
            </Button>
          </>
        )}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-hover" data-testid="add-employee-btn">
              <Plus size={18} className="mr-2" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
              <DialogDescription>
                Enter the employee details below
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emp_code">Employee Code</Label>
                  <Input
                    id="emp_code"
                    placeholder="PG0001"
                    value={newEmployee.employee_code}
                    onChange={(e) => setNewEmployee({ ...newEmployee, employee_code: e.target.value })}
                    data-testid="emp-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Select 
                    value={newEmployee.company_id} 
                    onValueChange={(v) => setNewEmployee({ ...newEmployee, company_id: v })}
                  >
                    <SelectTrigger data-testid="company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    placeholder="John"
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
                    data-testid="first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    placeholder="Doe"
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
                    data-testid="last-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john.doe@prideglobal.com"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  data-testid="email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    placeholder="Engineering"
                    value={newEmployee.department}
                    onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                    data-testid="department-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    placeholder="Software Engineer"
                    value={newEmployee.designation}
                    onChange={(e) => setNewEmployee({ ...newEmployee, designation: e.target.value })}
                    data-testid="designation-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="join_date">Join Date</Label>
                <Input
                  id="join_date"
                  type="date"
                  value={newEmployee.join_date}
                  onChange={(e) => setNewEmployee({ ...newEmployee, join_date: e.target.value })}
                  data-testid="join-date-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateEmployee} disabled={submitting} data-testid="save-employee-btn">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Employee
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search employees..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-employees"
          />
        </div>
        <Select value={filterCompany} onValueChange={setFilterCompany}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-company">
            <Building2 size={16} className="mr-2" />
            <SelectValue placeholder="All Companies" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(company => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
            <Filter size={16} className="mr-2" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="on_leave">On Leave</SelectItem>
            <SelectItem value="resigned">Resigned</SelectItem>
            <SelectItem value="terminated">Terminated</SelectItem>
            <SelectItem value="transferred">Transferred</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="absconded">Absconded</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
            <SelectItem value="contract_ended">Contract Ended</SelectItem>
            <SelectItem value="deceased">Deceased</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Employee Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => {
          const hasLogin = !!accountStatus[employee.id];
          const isSelected = selectedIds.has(employee.id);
          const eligible = eligibleForAccountCreation(employee);
          const cardInner = (
            <Card className={`card-hover cursor-pointer h-full ${selectionMode && isSelected ? 'ring-2 ring-primary' : ''} ${selectionMode && !eligible ? 'opacity-60' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {selectionMode && (
                    <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => eligible && toggleSelect(employee.id)}
                        disabled={!eligible}
                        data-testid={`bulk-checkbox-${employee.employee_code}`}
                      />
                    </div>
                  )}
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={employee.photo_url || employee.avatar_url} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(employee.first_name, employee.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold truncate">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <Badge variant={getStatusBadgeVariant(employee.status)}>
                        {employee.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{employee.employee_code}</p>
                    {canManage && (
                      <div className="mt-1">
                        {hasLogin ? (
                          <Badge variant="success" className="text-xs" data-testid={`has-login-${employee.employee_code}`}>
                            <KeyRound size={10} className="mr-1" />Has Login
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs" data-testid={`no-login-${employee.employee_code}`}>
                            No Login
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Briefcase size={14} className="mr-2" />
                    <span className="truncate">{employee.designation}</span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Building2 size={14} className="mr-2" />
                    <span className="truncate">{getCompanyName(employee.company_id)}</span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Mail size={14} className="mr-2" />
                    <span className="truncate">{employee.email}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          return selectionMode ? (
            <div key={employee.id} onClick={() => eligible && toggleSelect(employee.id)} data-testid={`employee-select-${employee.employee_code}`}>
              {cardInner}
            </div>
          ) : (
            <Link key={employee.id} to={`/employees/${employee.id}`} data-testid={`employee-card-${employee.employee_code}`}>
              {cardInner}
            </Link>
          );
        })}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No employees found</p>
        </div>
      )}

      {/* Bulk Account Creation Preview / Results Dialog */}
      <Dialog open={bulkPreviewOpen} onOpenChange={(o) => { if (!o) closeBulkAll(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="bulk-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={18} />
              {bulkResult ? 'Bulk Creation Results' : 'Confirm Bulk Login Creation'}
            </DialogTitle>
            <DialogDescription>
              {bulkResult
                ? `${bulkResult.created} account(s) created, ${bulkResult.skipped} skipped, ${bulkResult.failed} failed.`
                : `Creating login accounts for ${previewCreatable.length} employee(s). ${previewSkippable.length} already have logins and ${previewInvalid.length} have no email — they will be skipped.`}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto space-y-3 pr-1">
            {!bulkResult ? (
              <>
                {previewCreatable.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1 flex items-center gap-1"><CheckCircle2 size={14} className="text-green-600" />To be created ({previewCreatable.length})</p>
                    <div className="max-h-48 overflow-y-auto border rounded divide-y">
                      {previewCreatable.map((e) => (
                        <div key={e.id} className="p-2 text-sm flex justify-between" data-testid={`preview-create-${e.employee_code}`}>
                          <span>{e.first_name} {e.last_name} <span className="text-muted-foreground">({e.employee_code})</span></span>
                          <span className="text-xs text-muted-foreground truncate">{e.email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {previewSkippable.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1 flex items-center gap-1 text-muted-foreground"><XCircle size={14} />Will be skipped — already has login ({previewSkippable.length})</p>
                    <div className="max-h-32 overflow-y-auto border rounded divide-y">
                      {previewSkippable.map((e) => (
                        <div key={e.id} className="p-2 text-xs text-muted-foreground">
                          {e.first_name} {e.last_name} ({e.employee_code})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {previewInvalid.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1 flex items-center gap-1 text-red-600"><XCircle size={14} />Missing email — cannot be created ({previewInvalid.length})</p>
                    <div className="max-h-32 overflow-y-auto border rounded divide-y">
                      {previewInvalid.map((e) => (
                        <div key={e.id} className="p-2 text-xs text-red-600">
                          {e.first_name} {e.last_name} ({e.employee_code})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="max-h-96 overflow-y-auto border rounded divide-y">
                  {(bulkResult.results || []).map((r, i) => (
                    <div key={i} className="p-2 text-sm flex items-start gap-2" data-testid={`bulk-result-row-${i}`}>
                      {r.status === 'created' && <CheckCircle2 size={16} className="text-green-600 mt-0.5" />}
                      {r.status === 'skipped' && <XCircle size={16} className="text-muted-foreground mt-0.5" />}
                      {r.status === 'failed' && <XCircle size={16} className="text-red-600 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <span className="truncate">{r.email || r.employee_id}</span>
                          <Badge variant={r.status === 'created' ? 'success' : (r.status === 'failed' ? 'destructive' : 'secondary')}>{r.status}</Badge>
                        </div>
                        {r.temp_password && (
                          <div className="text-xs text-muted-foreground font-mono">Temp password: <span className="font-semibold">{r.temp_password}</span></div>
                        )}
                        {r.error && <div className="text-xs text-red-600">{r.error}</div>}
                        {r.status === 'skipped' && r.reason && <div className="text-xs text-muted-foreground">{r.reason}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Note: temporary passwords are shown once. If email delivery is not configured, please share them securely with employees.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={closeBulkAll} data-testid="bulk-cancel-btn">Cancel</Button>
                <Button onClick={runBulkCreate} disabled={bulkBusy || previewCreatable.length === 0} data-testid="bulk-confirm-btn">
                  {bulkBusy && <Loader2 size={14} className="animate-spin mr-2" />}
                  Create {previewCreatable.length} Account(s)
                </Button>
              </>
            ) : (
              <Button onClick={closeBulkAll} data-testid="bulk-close-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
